import classnames from 'classnames';

import { Button, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ClearIcon from '@mui/icons-material/Clear';

import Typography from '@classify/react-core-components/Typography';

import { palette as themePalette } from '../../../theme';

const useStyles = makeStyles(
  ({ palette, shadows }) => {
    const borderRadius = 16;

    return {
      root: {
        display: 'inline-block',
        borderRadius,
      },

      button: {
        paddingTop: 0,
        paddingBottom: 0,
        border: '1px solid transparent',
        borderRadius,
        color: palette.text.onContrasted,
        minHeight: 32,
        position: 'relative',
      },

      text: {
        textTransform: 'none',
      },

      isOnContrasted: {
        boxShadow: shadows[1],
      },

      selected: {
        '& $button': {
          backgroundColor: 'transparent',
          '&:focus': {
            boxShadow: shadows[6],
          },
        },
      },

      disabled: {
        '& $button': {
          opacity: 0.38,
        },
        '& $isOnContrasted': {
          opacity: 0.64,
        },
      },
      withoutClick: {
        cursor: 'default',
      },
    };
  },
  { classNamePrefix: 'Chip' }
);

/**
 * @deprecated
 * ### Description
 * `Chip` has been deprecated and replaced by Classify `Chip`.
 *
 * ### Example
 * ```
 * import Chip from '@classify/react-core-components/Chip';
 *
 * <Chip label="Label" selected />
 * ```
 */
export default function Chip({
  label = '',
  isOnContrasted = false,
  selected = false,
  color = themePalette.text.onMain,
  backgroundColor = themePalette.background.alternative,
  borderColor = themePalette.stroke,
  disabled = false,
  onClick,
  onDelete,
  role = null,
  tabIndex = null,
  onKeyDown = () => {},
  forwardedRef = null,
  ariaLabel = 'Delete entry',
  ...otherProps
}) {
  const style = {
    backgroundColor,
    color,
    borderColor,
  };

  const isRadioButton = role === 'radio';

  const classes = useStyles();

  const onDeleteHandler = (e) => {
    e.stopPropagation();
    onDelete();
  };

  const endIcon = onDelete ? (
    <IconButton
      data-testid="deleteButton"
      style={{ color: style.color }}
      component={onClick ? 'span' : 'button'}
      onClick={onDeleteHandler}
      size="small"
      aria-label={ariaLabel}
    >
      <ClearIcon fontSize="small" />
    </IconButton>
  ) : null;

  return (
    <span
      className={classnames(classes.root, {
        [classes.selected]: selected,
        [classes.disabled]: disabled,
      })}
    >
      <Button
        ref={forwardedRef}
        role={role}
        tabIndex={tabIndex}
        disabled={disabled}
        aria-checked={isRadioButton ? selected : null}
        style={style}
        className={classnames(classes.button, {
          [classes.selected]: selected,
          [classes.isOnContrasted]: isOnContrasted,
          [classes.withoutClick]: !onClick,
        })}
        onClick={onClick}
        onKeyDown={onKeyDown}
        disableFocusRipple
        disableRipple={!onClick}
        component={onClick ? 'button' : 'span'}
        endIcon={endIcon}
        {...otherProps}
      >
        <Typography style={{ color }} className={classes.text} variant="chip" component="span">
          {label}
        </Typography>
      </Button>
    </span>
  );
}
